class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('click', (event) => {
  const upsellButton = event.target.closest('[data-cart-upsell-add]');
  if (!upsellButton || !upsellButton.closest('cart-drawer')) return;

  event.preventDefault();
  const variantId = upsellButton.dataset.variantId;
  if (!variantId) return;

  upsellButton.disabled = true;
  upsellButton.textContent = 'Adding…';

  fetch(`${routes.cart_add_url}`, {
    ...fetchConfig('javascript'),
    body: JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
    }),
  })
    .then((response) => response.json())
    .then(() => {
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems) cartDrawerItems.onCartUpdate();
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      upsellButton.disabled = false;
      upsellButton.textContent = 'Add';
    });
});

document.addEventListener('submit', async (event) => {
  const discountForm = event.target.closest('[data-cart-discount-form]');
  if (!discountForm || !discountForm.closest('cart-drawer')) return;

  event.preventDefault();

  const input = discountForm.querySelector('[name="discount"]');
  const button = discountForm.querySelector('button[type="submit"]');
  const message = discountForm.querySelector('[data-cart-discount-message]');
  const code = input?.value.trim();
  if (!input || !button || !message || !code) return;

  button.disabled = true;
  button.textContent = 'Applying…';
  message.hidden = true;
  message.classList.remove('is-error', 'is-success');

  try {
    const response = await fetch(routes.cart_update_url || '/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        discount: code,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    });

    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || cart.message || 'Unable to apply coupon code.');

    const normalizedCode = code.toLowerCase();
    const matchingCode = (cart.discount_codes || []).find(
      (discount) => discount.code?.toLowerCase() === normalizedCode && discount.applicable !== false
    );
    const matchingApplication = (cart.cart_level_discount_applications || []).find(
      (discount) => discount.title?.toLowerCase() === normalizedCode
    );

    if (!matchingCode && !matchingApplication) {
      throw new Error('This coupon code is invalid or is not applicable to your cart.');
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cart.sections) {
      cartDrawer.renderContents(cart);
      requestAnimationFrame(() => {
        const updatedForm = cartDrawer.querySelector('[data-cart-discount-form]');
        const updatedInput = updatedForm?.querySelector('[name="discount"]');
        const updatedMessage = updatedForm?.querySelector('[data-cart-discount-message]');
        if (updatedInput) updatedInput.value = code.toUpperCase();
        if (updatedMessage) {
          updatedMessage.textContent = 'Coupon code applied successfully.';
          updatedMessage.classList.add('is-success');
          updatedMessage.hidden = false;
        }
      });
    } else {
      document.querySelector('cart-drawer-items')?.onCartUpdate();
    }
  } catch (error) {
    message.textContent = error.message;
    message.classList.add('is-error');
    message.hidden = false;
  } finally {
    if (button.isConnected) {
      button.disabled = false;
      button.textContent = 'Apply';
    }
  }
});
